import { useState, useEffect, useCallback } from 'react';
import noteService from '../../../../../utils/apiRequests/noteService';

const matchesChapter = (note, chapterId) => {
  if (!note || !chapterId) return false;
  const id = String(chapterId);
  if (note.parentType === 'chapter' && String(note.parentId) === id) return true;
  return (note.relatedChapters || []).map(String).includes(id);
};

/**
 * Loads and manages notes attached to a specific chapter.
 * Notes are linked either via parentType/parentId or relatedChapters.
 */
const useChapterNotes = (projectId, chapterId) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    if (!projectId || !chapterId) {
      setNotes([]);
      return;
    }
    setLoading(true);
    setError('');
    const response = await noteService.getNotes(projectId, { useCache: false });
    if (response.error) {
      setError('Failed to load notes');
    } else {
      const all = response.data?.notes || [];
      setNotes(all.filter((note) => matchesChapter(note, chapterId)));
    }
    setLoading(false);
  }, [projectId, chapterId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const createNote = useCallback(
    async (fields) => {
      if (!projectId || !chapterId) return { error: new Error('No chapter selected') };
      const response = await noteService.createNote(projectId, {
        featureContext: 'chapter',
        parentType: 'chapter',
        parentId: chapterId,
        relatedChapters: [String(chapterId)],
        ...fields,
      });
      const created = response.data?.data || response.data;
      if (!response.error && created && created.title) {
        setNotes((prev) => [created, ...prev]);
      }
      return response;
    },
    [projectId, chapterId]
  );

  const updateNote = useCallback(
    async (noteId, updates) => {
      const response = await noteService.updateNote(projectId, noteId, updates);
      const updated = response.data?.data || response.data;
      if (!response.error) {
        setNotes((prev) =>
          prev.map((note) =>
            String(note._id || note.id) === String(noteId)
              ? { ...note, ...updates, ...(updated && updated.title ? updated : {}) }
              : note
          )
        );
      }
      return response;
    },
    [projectId]
  );

  const deleteNote = useCallback(
    async (noteId) => {
      const response = await noteService.deleteNote(projectId, noteId);
      if (!response.error) {
        setNotes((prev) => prev.filter((note) => String(note._id || note.id) !== String(noteId)));
      }
      return response;
    },
    [projectId]
  );

  return { notes, loading, error, refresh, createNote, updateNote, deleteNote };
};

export default useChapterNotes;
